/* A gallery photo in the manager.
 *
 * Tries the public CDN URL first — that is what a visitor sees, so previewing it
 * is the honest thing — and falls back to the authenticated Worker proxy when
 * that fails. Two situations need the fallback and neither is exotic:
 *
 *   • Local development. R2 is Miniflare on the developer's disk while
 *     CDN_BASE_URL still points at the real CDN, so every thumbnail 404s.
 *   • A photo uploaded seconds ago, anywhere, that has not propagated yet.
 *
 * The blob URL is revoked on unmount; leaking one per thumbnail adds up in a
 * gallery someone leaves open all afternoon.
 */
import { useEffect, useRef, useState } from 'react';
import { apiBlobUrl } from '@/data/api';

export function AssetImg({ url, assetId, alt, className, fallbackPath }: {
  /** Public CDN URL. Tried first; may be '' when the photo has no public copy. */
  url: string;
  /** Falls back to /api/studio/gallery-assets/<id> when the CDN URL fails. */
  assetId: string;
  alt: string;
  className?: string;
  /** Overrides the proxy above, for callers outside Expert Studio's self-service gate. */
  fallbackPath?: string;
}) {
  const [src, setSrc] = useState(url);
  const [failed, setFailed] = useState(false);
  const objectUrl = useRef('');

  useEffect(() => { setSrc(url); setFailed(false); }, [url, assetId]);

  useEffect(() => () => { if (objectUrl.current) URL.revokeObjectURL(objectUrl.current); }, []);

  const fallBack = () => {
    if (failed || !assetId) { setFailed(true); return; }
    setFailed(true);
    apiBlobUrl(fallbackPath ?? `/api/studio/gallery-assets/${encodeURIComponent(assetId)}`)
      .then((blob) => { objectUrl.current = blob; setSrc(blob); })
      .catch(() => setSrc(''));
  };

  // No source at all yet — an upload still in flight, or bytes that are gone.
  if (!src) return <span className={className} aria-hidden="true" />;

  return <img className={className} src={src} alt={alt} loading="lazy" onError={fallBack} />;
}
